import React, { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { BRAND } from "@/lib/brand";
import { LANDING_PAGES } from "@/lib/landingPages";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, MessageCircle, CheckCircle2, ArrowRight, ShieldCheck, Award, MapPin, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ACCENT = {
  navy:    { from: "#0A2540", to: "#11365E", chip: "bg-white/10 text-white", btn: "btn-gold" },
  emerald: { from: "#0B7C46", to: "#0F9D58", chip: "bg-white/10 text-white", btn: "btn-gold" },
  gold:    { from: "#0A2540", to: "#7a6d36", chip: "bg-white/10 text-white", btn: "btn-emerald" },
};

export default function LandingPage() {
  const { slug } = useParams();
  const lp = LANDING_PAGES[slug];

  const [form, setForm] = useState({ name: "", phone: "", insurer: "", message: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (lp) document.title = `${lp.title} — ${BRAND.name}`;
  }, [lp]);

  if (!lp) return <Navigate to="/" replace />;
  const accent = ACCENT[lp.accent] || ACCENT.navy;

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const txt =
`Hello Accurate Claims,
Source: ${lp.keyword}
Name: ${form.name}
Phone: ${form.phone}
Insurer: ${form.insurer}

${form.message}`;
    const url = `${BRAND.whatsappUrl.split("?")[0]}?text=${encodeURIComponent(txt)}`;
    window.open(url, "_blank");
    setSent(true);
    toast.success("Sending via WhatsApp");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Minimal LP header — logo + phone only */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" data-testid="lp-logo" className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-heading font-bold text-[15px] text-navy">ACCURATE CLAIMS</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">Claim Advisory</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <a href={BRAND.telUrl} data-testid="lp-header-call" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-emerald-brand">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="lp-header-whatsapp" className="btn-emerald !py-2 !px-4 text-sm">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)` }} />
        <div className="absolute inset-0 bg-dot-grid opacity-10" />
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start text-white">
          <div className="lg:col-span-7">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${accent.chip}`}>
              <ShieldCheck className="w-3.5 h-3.5" /> Independent · Free first review · Gujarat-headquartered
            </div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.05]">
              {lp.title}
            </h1>
            <p className="mt-5 text-slate-100 leading-relaxed text-lg max-w-2xl">
              {lp.subtitle}
            </p>

            <ul className="mt-7 space-y-2.5 max-w-xl">
              {lp.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px]">
                  <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#lp-form" data-testid="lp-hero-primary-cta" className={`${accent.btn} text-sm`}>
                {lp.primaryCta} <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://fact-based-challenge.emergent.host"
                target="_blank"
                rel="noreferrer"
                data-testid="lp-hero-ai-eval"
                className="px-5 py-3 rounded-full bg-white/15 border-2 border-white/40 text-white hover:bg-white/25 text-sm font-semibold inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4 text-gold" /> AI Claim Evaluation
              </a>
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="lp-hero-whatsapp" className="px-5 py-3 rounded-full border-2 border-white/40 text-white hover:bg-white/10 text-sm font-semibold inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
              <a href={BRAND.telUrl} data-testid="lp-hero-call" className="px-5 py-3 rounded-full bg-white text-navy text-sm font-semibold inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call {BRAND.phone}
              </a>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {lp.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-heading font-bold text-3xl md:text-4xl text-gold">{s.value}</div>
                  <div className="text-xs text-slate-200 mt-1.5 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Form card */}
          <div className="lg:col-span-5" id="lp-form">
            <form onSubmit={submit} data-testid="lp-form" className="bg-white rounded-2xl text-slate-900 p-7 brand-shadow-lg">
              {sent ? (
                <div className="py-8 text-center">
                  <div className="w-14 h-14 mx-auto rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading font-bold text-xl mt-5">Sending on WhatsApp</h3>
                  <p className="text-slate-600 text-sm mt-2">A new tab should have opened. If not, tap below.</p>
                  <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="lp-form-open-whatsapp" className="btn-emerald text-sm mt-5 inline-flex">
                    <MessageCircle className="w-4 h-4" /> Open WhatsApp
                  </a>
                </div>
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-brand font-semibold">Free claim review</div>
                  <h3 className="font-heading font-bold text-2xl mt-1">{lp.formTitle}</h3>
                  <div className="space-y-3 mt-5">
                    <Field label="Your name">
                      <Input required value={form.name} onChange={setField("name")} data-testid="lp-form-name" className="rounded-xl h-11" />
                    </Field>
                    <Field label="Phone (WhatsApp)">
                      <Input required value={form.phone} onChange={setField("phone")} data-testid="lp-form-phone" className="rounded-xl h-11" />
                    </Field>
                    <Field label="Insurer name">
                      <Input value={form.insurer} onChange={setField("insurer")} placeholder="e.g. Star Health, HDFC ERGO" data-testid="lp-form-insurer" className="rounded-xl h-11" />
                    </Field>
                    <Field label="Brief about your case">
                      <Textarea required rows={4} value={form.message} onChange={setField("message")} data-testid="lp-form-message" className="rounded-xl" />
                    </Field>
                  </div>
                  <button type="submit" data-testid="lp-form-submit" className="btn-primary w-full text-sm mt-5">
                    {lp.primaryCta} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[11px] text-slate-500 mt-3 text-center">We respond within 24 working hours. Your data stays private.</p>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Proof / mini trust strip */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-8 grid md:grid-cols-3 gap-6 items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center"><Award className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-semibold text-sm">20+ years</div>
              <div className="text-xs text-slate-500">in claim advisory</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-semibold text-sm">Independent</div>
              <div className="text-xs text-slate-500">we never sell insurance</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-navy/10 text-navy flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
            <div>
              <div className="font-heading font-semibold text-sm">Ahmedabad office</div>
              <div className="text-xs text-slate-500">Ratna Business Square, Ashram Road</div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof card */}
      <section className="max-w-[900px] mx-auto px-5 md:px-8 py-14">
        <div className="brand-card p-8 md:p-10">
          <div className="flex text-gold mb-4">
            {[0,1,2,3,4].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}
          </div>
          <p className="text-lg md:text-xl font-heading text-slate-800 leading-snug">"{lp.proof}"</p>
          <div className="mt-5 text-xs uppercase tracking-wider text-slate-500 font-semibold">— Recent client outcome</div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-8 pb-16">
        <div className="rounded-[24px] p-10 md:p-14 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)` }}>
          <div className="absolute inset-0 bg-dot-grid opacity-10" />
          <div className="relative grid md:grid-cols-12 items-center gap-6">
            <div className="md:col-span-7">
              <h2 className="font-heading font-bold text-2xl md:text-4xl leading-tight">
                Get a free expert review in 48 hours.
              </h2>
              <p className="mt-3 text-slate-200">No obligation. No charges for the first review. Senior advisor with 20+ years.</p>
            </div>
            <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
              <a href="#lp-form" data-testid="lp-bottom-form-cta" className="btn-gold text-sm">Start now <ArrowRight className="w-4 h-4" /></a>
              <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="lp-bottom-whatsapp" className="btn-emerald text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Slim footer for LP — compliance only, no nav distractions */}
      <footer className="bg-slate-100 border-t border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-6 text-xs text-slate-500 flex flex-col md:flex-row gap-3 justify-between">
          <div>© {new Date().getFullYear()} {BRAND.name} · Ahmedabad, Gujarat</div>
          <div>An independent claim advisory. We do not sell insurance. Legal proceedings are handled by qualified advocates we coordinate with.</div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
